import { toActionPlanCalendarDate, formatDateOnlyLocalYMD } from "@/utils/dateOnlyForApi";
import dayjs, { type Dayjs } from "dayjs";

/**
 * Chuẩn hóa ngày form / API → Date local midnight đúng ngày lịch.
 */
export const validateAndConvertDate = (dateValue: unknown): Date | null => {
  if (!dateValue) return null;

  try {
    const date =
      typeof dateValue === 'string'
        ? toActionPlanCalendarDate(dateValue)
        : toActionPlanCalendarDate(dayjs(dateValue as dayjs.ConfigType).format('YYYY-MM-DD'));
    if (!date) return null;

    const minDate = new Date(1753, 0, 1);
    const maxDate = new Date(9999, 11, 31);

    if (date < minDate || date > maxDate || Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch {
    return null;
  }
};

/**
 * Date-only gửi API dạng "YYYY-MM-DD".
 * Không dùng Date object — JSON.stringify(Date) → toISOString() lệch timezone (VN -7h).
 */
export const toApiDateOnlyString = (dateValue: unknown): string | null => {
  if (dateValue == null || dateValue === '') return null;

  try {
    if (dayjs.isDayjs(dateValue)) {
      return dateValue.isValid() ? dateValue.format('YYYY-MM-DD') : null;
    }

    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      // Đã lưu lệch dạng ...T17:00:00(.000)?Z? → coi là UTC, lấy ngày lịch local (VN)
      if (/^\d{4}-\d{2}-\d{2}T17:00:00/.test(trimmed)) {
        const utc = dayjs(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`);
        return utc.isValid() ? utc.format('YYYY-MM-DD') : null;
      }
      const ymd = formatDateOnlyLocalYMD(trimmed);
      return ymd;
    }

    if (dateValue instanceof Date) {
      return formatDateOnlyLocalYMD(dateValue);
    }

    const parsed = dayjs(dateValue as dayjs.ConfigType);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  } catch {
    return null;
  }
};

/**
 * Parse ngày từ API vào DatePicker — xử lý lệch T17:00:00 (UTC midnight VN).
 */
export const parseApiDateOnlyToDayjs = (dateValue: unknown): Dayjs | null => {
  if (dateValue == null || dateValue === '') return null;

  try {
    if (dayjs.isDayjs(dateValue)) {
      return dateValue.isValid() ? dateValue : null;
    }

    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (/^\d{4}-\d{2}-\d{2}T17:00:00/.test(trimmed)) {
        const utc = dayjs(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`);
        return utc.isValid() ? utc : null;
      }
      const ymdMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (ymdMatch) {
        const d = dayjs(ymdMatch[1]);
        return d.isValid() ? d.startOf('day') : null;
      }
    }

    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return dayjs(dateValue);
    }

    const parsed = dayjs(dateValue as dayjs.ConfigType);
    return parsed.isValid() ? parsed : null;
  } catch {
    return null;
  }
};

/** Hiển thị DD/MM/YYYY từ giá trị API date-only (kể cả T17:00:00). */
export const formatApiDateOnlyDisplay = (dateValue: unknown): string => {
  const d = parseApiDateOnlyToDayjs(dateValue);
  return d ? d.format('DD/MM/YYYY') : '';
};

/** Hiển thị HH:mm DD/MM/YYYY (UTC+7) từ giá trị API datetime. */
export const formatApiDateTimeDisplay = (dateValue: unknown): string => {
  if (!dateValue) return '';
  try {
    let d: Dayjs | null = null;
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      // Xử lý ISO string: "2024-01-15T10:30:00" → cộng 7h cho UTC+7
      if (trimmed.includes('T')) {
        d = dayjs(trimmed).add(7, 'hour');
      } else {
        d = parseApiDateOnlyToDayjs(trimmed);
      }
    } else if (dateValue instanceof Date) {
      d = dayjs(dateValue).add(7, 'hour');
    } else {
      d = dayjs(dateValue as dayjs.ConfigType).add(7, 'hour');
    }
    return d && d.isValid() ? d.format('HH:mm DD/MM/YYYY') : '';
  } catch {
    return '';
  }
};